import React, { useState, useEffect, useMemo, useRef } from "react";
import api, { fmtVND, fmtFull } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Wallet, FileDown, TrendingUp, TrendingDown, Users, Banknote, CreditCard } from "lucide-react";
import ApexChart from "react-apexcharts";

// Helper: get first + last name only (no middle) - used in Tổng quan charts
function shortName(fullName) {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 2) return fullName;
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

export default function SalaryTab() {
  const [records, setRecords] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState([]);
  const [deptStats, setDeptStats] = useState([]);
  const [latestMonth, setLatestMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [dept, setDept] = useState("");
  const [searchName, setSearchName] = useState("");
  const [sortCol, setSortCol] = useState("stt");
  const [sortAsc, setSortAsc] = useState(true);
  const [subTab, setSubTab] = useState("bang_luong");
  const [initialized, setInitialized] = useState(false);
  const [advances, setAdvances] = useState(null);
  const [prevMonthRecords, setPrevMonthRecords] = useState([]);

  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);

  const handleTopScroll = () => {
    if (tableScrollRef.current && topScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };
  const handleTableScroll = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };

  const MONTH_PRESETS = (() => {
    const now = new Date();
    const months = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ value: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, label: `T${d.getMonth()+1}/${d.getFullYear()}` });
    }
    return months;
  })();

  // First load: get summary to find latest month
  useEffect(() => {
    api.get("/accounting/salary-summary").then(({ data }) => {
      setSummary(data.months || []);
      setDeptStats(data.departments || []);
      const lm = data.latest_month || "";
      setLatestMonth(lm);
      if (lm && !initialized) {
        setMonth(lm);
        setInitialized(true);
      }
    }).catch(() => {});
  }, []);

  // Load records when month changes
  useEffect(() => {
    if (!month) return;
    setLoading(true);
    // Calculate previous month
    const [y, m] = month.split("-").map(Number);
    const prevDate = new Date(y, m - 2, 1);
    const prevMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, "0")}`;

    Promise.all([
      api.get(`/accounting/salary?month=${month}&department=${dept}&search=${searchName}`),
      api.get(`/accounting/salary/advances?month=${month}`),
      api.get(`/accounting/salary?month=${prevMonth}`),
    ]).then(([salRes, advRes, prevRes]) => {
      setRecords(salRes.data.records || []);
      setDepartments(salRes.data.departments || []);
      setAdvances(advRes.data);
      setPrevMonthRecords(prevRes.data.records || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [month, dept, searchName]);

  // Only show records from "BẢNG LƯƠNG..." sheets + dedupe by ma_nv
  const deduped = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      const code = r.ma_nv || r.employee_code || "";
      if (!code) continue;
      const deptName = (r.department || "").toUpperCase();
      if (!deptName.includes("BẢNG LƯƠNG") && !deptName.includes("BANG LUONG")) continue;
      const key = `${code}_${r.month}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, r);
      } else {
        const scoreNew = (r.muc_luong || 0) + (r.tong_luong || 0) + (r.so_cong ? 1000 : 0);
        const scoreOld = (existing.muc_luong || 0) + (existing.tong_luong || 0) + (existing.so_cong ? 1000 : 0);
        if (scoreNew > scoreOld) map.set(key, r);
      }
    }
    return Array.from(map.values());
  }, [records]);

  // Build lookup map for previous month salary by employee code/name
  const prevSalaryMap = useMemo(() => {
    const map = new Map();
    for (const r of prevMonthRecords) {
      const code = r.ma_nv || r.employee_code || "";
      const deptName = (r.department || "").toUpperCase();
      if (!deptName.includes("BẢNG LƯƠNG") && !deptName.includes("BANG LUONG")) continue;
      if (code) map.set(code, r);
    }
    return map;
  }, [prevMonthRecords]);

  // Get % change for an employee vs previous month
  const getChangePercent = (r) => {
    const code = r.ma_nv || r.employee_code || "";
    const prev = prevSalaryMap.get(code);
    if (!prev) return null;
    const curSalary = r.thuc_nhan || r.net_salary || r.tong_luong || 0;
    const prevSalary = prev.thuc_nhan || prev.net_salary || prev.tong_luong || 0;
    if (prevSalary === 0) return null;
    return ((curSalary - prevSalary) / prevSalary * 100).toFixed(2);
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(true); }
  };

  const sortedRecords = useMemo(() => {
    return [...deduped].sort((a, b) => {
      const va = a[sortCol] ?? 0;
      const vb = b[sortCol] ?? 0;
      if (typeof va === "string") return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortAsc ? va - vb : vb - va;
    });
  }, [deduped, sortCol, sortAsc]);

  const totalNet = deduped.reduce((a, r) => a + (r.thuc_nhan || r.net_salary || 0), 0);
  const totalBase = deduped.reduce((a, r) => a + (r.muc_luong || r.base_salary || 0), 0);
  const totalAllowance = deduped.reduce((a, r) => a + (r.phu_cap || r.phu_cap_luong || 0), 0);

  // Previous month comparison
  const prevMonthData = useMemo(() => {
    const idx = summary.findIndex(s => s.month === month);
    if (idx > 0) return summary[idx - 1];
    return null;
  }, [summary, month]);

  const currentMonthData = useMemo(() => {
    return summary.find(s => s.month === month) || null;
  }, [summary, month]);

  const pctChange = (current, prev) => {
    if (!prev || prev === 0) return null;
    return ((current - prev) / prev * 100).toFixed(1);
  };

  const handleExport = async () => {
    try {
      const resp = await api.get(`/accounting/salary/export?month=${month}&department=${dept}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `bang_luong_${month}.xlsx`); document.body.appendChild(link);
      link.click(); link.remove(); toast.success("Da xuat Excel");
    } catch { toast.error("Loi xuat file"); }
  };

  const SortTh = ({ col, children, className = "" }) => (
    <th onClick={() => handleSort(col)} className={`py-1.5 px-1.5 font-medium cursor-pointer hover:text-lime-400 select-none whitespace-nowrap text-[10px] ${className}`}>
      <span className="inline-flex items-center gap-0.5">{children}
        {sortCol === col && <span className="text-lime-400 text-[8px]">{sortAsc ? "▲" : "▼"}</span>}
      </span>
    </th>
  );

  const SUB_TABS = [
    { key: "bang_luong", label: "Bảng lương" },
    { key: "tam_ung", label: `Tạm ứng${advances?.total_employees ? ` (${advances.total_employees})` : ""}` },
    { key: "phieu_luong", label: "Phiếu lương" },
    { key: "tong_hop", label: "Tổng hợp" },
  ];

  const fNum = (v) => {
    if (!v || v === 0) return "-";
    if (Number.isInteger(v)) return v.toLocaleString("vi-VN");
    return v.toFixed(1);
  };
  const fMoney = (v) => {
    if (!v || v === 0) return "-";
    return fmtFull(Math.round(v));
  };

  const kpiCards = [
    {
      label: "Tổng quỹ lương",
      value: fmtVND(totalNet),
      change: pctChange(currentMonthData?.total || 0, prevMonthData?.total || 0),
      period: "vs tháng trước",
      gradient: "from-[#4680ff] to-[#5b8def]",
      sparkData: summary.slice(-6).map(s => s.total || 0),
      icon: Banknote,
    },
    {
      label: "Nhân viên",
      value: `${deduped.length}`,
      change: pctChange(currentMonthData?.count || deduped.length, prevMonthData?.count || 0),
      period: "vs tháng trước",
      gradient: "from-[#05c3a6] to-[#22d6b4]",
      sparkData: summary.slice(-6).map(s => s.count || 0),
      icon: Users,
    },
    {
      label: "Lương trung bình",
      value: deduped.length > 0 ? fmtVND(Math.round(totalNet / deduped.length)) : "0",
      change: (() => {
        const curAvg = (currentMonthData?.total || 0) / Math.max(currentMonthData?.count || 1, 1);
        const prevAvg = (prevMonthData?.total || 0) / Math.max(prevMonthData?.count || 1, 1);
        return pctChange(curAvg, prevAvg);
      })(),
      period: "vs tháng trước",
      gradient: "from-[#7c3aed] to-[#a78bfa]",
      sparkData: summary.slice(-6).map(s => s.count > 0 ? Math.round(s.total / s.count) : 0),
      icon: TrendingUp,
    },
    {
      label: "Tổng phụ cấp",
      value: fmtVND(totalAllowance),
      change: pctChange(currentMonthData?.allowance_total || totalAllowance, prevMonthData?.allowance_total || 0),
      period: "vs tháng trước",
      gradient: "from-[#fd7e14] to-[#ff9a44]",
      sparkData: summary.slice(-6).map(s => s.allowance_total || 0),
      icon: Wallet,
    },
  ];

  return (
    <div className="space-y-4" data-testid="acct-salary">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2"><Wallet className="w-4 h-4 text-lime-400" /> Quản lý Lương</h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          {MONTH_PRESETS.slice(0, 4).map(p => (
            <button key={p.value} onClick={() => setMonth(p.value)} className={`px-2 py-1 rounded text-[10px] ${month === p.value ? "bg-indigo-500/20 text-indigo-400 font-bold" : "bg-[#1e293b] border border-[#334155] text-slate-400 hover:text-slate-200"}`}>{p.label}</button>
          ))}
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1 text-[10px] text-slate-300 h-7" />
        </div>
      </div>

      {/* KPI Cards - Reference Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="salary-kpi-cards">
        {kpiCards.map((card, i) => (
          <div key={i} className="relative overflow-hidden bg-[#1e293b] border border-[#334155] rounded-xl p-4 flex flex-col justify-between min-h-[120px]" data-testid={`salary-kpi-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{card.label}</span>
              <card.icon className="w-4 h-4 text-slate-500" />
            </div>
            <div className="flex items-end justify-between mt-2">
              <p className="text-xl font-bold text-slate-100">{card.value}</p>
              {/* Mini sparkline */}
              {card.sparkData.length > 1 && (
                <div className="w-[70px] h-[35px] opacity-70">
                  <ApexChart type="area" height={35} width={70}
                    options={{
                      chart: { sparkline: { enabled: true }, animations: { enabled: false } },
                      stroke: { curve: 'smooth', width: 1.5 },
                      fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0, stops: [0, 100] } },
                      colors: [card.change && parseFloat(card.change) < 0 ? '#ef4444' : '#84cc16'],
                      tooltip: { enabled: false },
                    }}
                    series={[{ data: card.sparkData }]}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              {card.change !== null && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${parseFloat(card.change) >= 0 ? "bg-lime-500/15 text-lime-400" : "bg-red-500/15 text-red-400"}`}>
                  {parseFloat(card.change) >= 0 ? "+" : ""}{card.change}%
                </span>
              )}
              <span className="text-[9px] text-slate-500">{card.period}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Tìm theo tên..." value={searchName} onChange={e => setSearchName(e.target.value)}
          className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs w-48" data-testid="salary-search" />
        <select value={dept} onChange={e => setDept(e.target.value)} className="bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1 text-xs text-slate-300 h-8">
          <option value="">Tất cả phòng ban</option>
          {departments.map(d => <option key={d} value={d}>{d.replace("BẢNG LƯƠNG ", "").replace("BẢNG LƯƠNG- ", "")}</option>)}
        </select>
        <Button onClick={handleExport} variant="outline" className="border-[#334155] text-slate-400 hover:text-lime-400 h-8 text-xs gap-1" data-testid="salary-export-btn">
          <FileDown className="w-3 h-3" /> Xuất Excel
        </Button>
        <div className="ml-auto flex items-center gap-1">
          {SUB_TABS.map(t => (
            <button key={t.key} onClick={() => setSubTab(t.key)} className={`px-2.5 py-1 rounded text-[10px] font-medium ${subTab === t.key ? "bg-violet-500/20 text-violet-400" : "text-slate-500 hover:text-slate-300"}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? <Loader2 className="w-5 h-5 text-lime-400 animate-spin mx-auto" /> :
      subTab === "bang_luong" ? (
        deduped.length === 0 ? <p className="text-slate-500 text-center py-8 text-sm">Chưa có dữ liệu lương tháng {month}. Nhập file Excel để bắt đầu.</p> :
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
          {/* Top scrollbar */}
          <div ref={topScrollRef} onScroll={handleTopScroll} className="overflow-x-auto" style={{ height: "12px" }}>
            <div style={{ width: "2400px", height: "1px" }}></div>
          </div>
          {/* Table */}
          <div ref={tableScrollRef} onScroll={handleTableScroll} className="overflow-x-auto">
            <table className="w-full text-[10px]" style={{ minWidth: "2400px" }}>
              <thead>
                {/* Tier 1: Group headers */}
                <tr className="border-b border-[#475569]" style={{ background: "linear-gradient(135deg, #1a2332 0%, #0f172a 100%)" }}>
                  <th colSpan={2} className="py-2 px-1 text-center text-[9px] font-bold text-slate-400 border-r border-[#475569]">#</th>
                  <th colSpan={5} className="py-2 px-1 text-center text-[9px] font-bold border-r border-[#475569]" style={{ color: "#94a3b8" }}>Thông tin nhân viên</th>
                  <th colSpan={4} className="py-2 px-1 text-center text-[9px] font-bold border-r border-[#475569]" style={{ background: "rgba(16,185,129,0.06)", color: "#34d399" }}>Ngày công</th>
                  <th colSpan={6} className="py-2 px-1 text-center text-[9px] font-bold border-r border-[#475569]" style={{ background: "rgba(56,189,248,0.06)", color: "#38bdf8" }}>Thu nhập</th>
                  <th colSpan={5} className="py-2 px-1 text-center text-[9px] font-bold border-r border-[#475569]" style={{ background: "rgba(248,113,113,0.06)", color: "#f87171" }}>Khấu trừ</th>
                  <th colSpan={2} className="py-2 px-1 text-center text-[9px] font-bold" style={{ background: "rgba(132,204,22,0.06)", color: "#84cc16" }}>Kết quả</th>
                </tr>
                {/* Tier 2: Column headers */}
                <tr className="border-b border-[#475569]" style={{ background: "#0c1524" }}>
                  <SortTh col="stt" className="text-center text-slate-500 w-[40px]">STT</SortTh>
                  <SortTh col="ma_nv" className="text-left text-slate-400">Mã NV</SortTh>
                  <SortTh col="ten" className="text-left text-slate-300">Tên NV</SortTh>
                  <SortTh col="chuc_vu" className="text-left text-slate-400">Chức vụ</SortTh>
                  <SortTh col="muc_luong" className="text-right text-slate-400">Mức lương</SortTh>
                  <SortTh col="luong_gio" className="text-right text-slate-400">Lương giờ</SortTh>
                  <SortTh col="phu_cap" className="text-right text-slate-400 border-r border-[#475569]">Phụ cấp</SortTh>
                  <SortTh col="so_cong" className="text-center text-emerald-400/80">Giờ TT</SortTh>
                  <SortTh col="so_gio_x2" className="text-center text-emerald-400/80">x2</SortTh>
                  <SortTh col="ngay_tru_30" className="text-center text-emerald-400/80">-30%</SortTh>
                  <SortTh col="nghi_phep" className="text-center text-emerald-400/80 border-r border-[#475569]">N.Phép</SortTh>
                  <SortTh col="phu_cap_luong" className="text-right text-sky-400/80">P.Cấp</SortTh>
                  <SortTh col="luong_gio_thuong" className="text-right text-sky-400/80">LG TT</SortTh>
                  <SortTh col="luong_gio_x2" className="text-right text-sky-400/80">LG x2</SortTh>
                  <SortTh col="tham_nien" className="text-right text-sky-400/80">T.Niên</SortTh>
                  <SortTh col="thuong" className="text-right text-sky-400/80">Thưởng</SortTh>
                  <SortTh col="tong_luong" className="text-right text-sky-300 font-bold border-r border-[#475569]">TỔNG</SortTh>
                  <SortTh col="bhxh" className="text-right text-red-400/80">BHXH</SortTh>
                  <SortTh col="dong_phuc" className="text-right text-red-400/80">Đ.Phục</SortTh>
                  <SortTh col="tam_ung" className="text-right text-red-400/80">T.Ứng</SortTh>
                  <SortTh col="phat" className="text-right text-red-400/80">Phạt</SortTh>
                  <SortTh col="khoan_khac" className="text-right text-red-400/80 border-r border-[#475569]">Khác</SortTh>
                  <SortTh col="thuc_nhan" className="text-right text-lime-400 font-bold">Thực nhận</SortTh>
                  <th className="py-1.5 px-1.5 text-center text-[9px] text-slate-500 font-medium">+/-%</th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.map((r, i) => {
                  const pctChange = getChangePercent(r);
                  const isEven = i % 2 === 0;
                  return (
                  <tr key={r.id || i} className={`border-b border-[#1e293b] hover:bg-[#1e293b] transition-colors ${isEven ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                    <td className="py-2 px-2 text-center text-slate-500 font-mono text-[9px]">{i + 1}</td>
                    <td className="py-2 px-1.5 text-sky-400 font-mono text-[9px]">{r.ma_nv || ""}</td>
                    <td className="py-2 px-1.5 text-slate-100 font-medium whitespace-nowrap">{r.ten || r.employee_name || ""}</td>
                    <td className="py-2 px-1.5 text-slate-400">{r.chuc_vu || r.position || ""}</td>
                    <td className="py-2 px-1.5 text-right text-slate-200">{fMoney(r.muc_luong)}</td>
                    <td className="py-2 px-1.5 text-right text-slate-400">{fNum(r.luong_gio)}</td>
                    <td className="py-2 px-1.5 text-right text-emerald-400 border-r border-[#1e293b]">{fMoney(r.phu_cap)}</td>
                    <td className="py-2 px-1.5 text-center text-slate-200">{fNum(r.so_cong)}</td>
                    <td className="py-2 px-1.5 text-center text-slate-400">{fNum(r.so_gio_x2)}</td>
                    <td className="py-2 px-1.5 text-center text-slate-400">{fNum(r.ngay_tru_30)}</td>
                    <td className="py-2 px-1.5 text-center text-slate-400 border-r border-[#1e293b]">{fNum(r.nghi_phep)}</td>
                    <td className="py-2 px-1.5 text-right text-sky-300">{fMoney(r.phu_cap_luong || r.phu_cap)}</td>
                    <td className="py-2 px-1.5 text-right text-sky-300">{fMoney(r.luong_gio_thuong)}</td>
                    <td className="py-2 px-1.5 text-right text-sky-300">{fMoney(r.luong_gio_x2)}</td>
                    <td className="py-2 px-1.5 text-right text-amber-400">{fMoney(r.tham_nien)}</td>
                    <td className="py-2 px-1.5 text-right text-lime-400">{fMoney(r.thuong)}</td>
                    <td className="py-2 px-1.5 text-right text-slate-100 font-semibold border-r border-[#1e293b]">{fMoney(r.tong_luong)}</td>
                    <td className="py-2 px-1.5 text-right text-red-400">{(r.bhxh || 0) > 0 ? `-${fMoney(r.bhxh)}` : "-"}</td>
                    <td className="py-2 px-1.5 text-right text-red-400">{(r.dong_phuc || 0) > 0 ? `-${fMoney(r.dong_phuc)}` : "-"}</td>
                    <td className="py-2 px-1.5 text-right text-orange-400">{(r.tam_ung || 0) > 0 ? `-${fMoney(r.tam_ung)}` : "-"}</td>
                    <td className="py-2 px-1.5 text-right text-red-400">{(r.phat || 0) > 0 ? `-${fMoney(r.phat)}` : "-"}</td>
                    <td className="py-2 px-1.5 text-right text-slate-500 border-r border-[#1e293b]">{(r.khoan_khac || 0) !== 0 ? fMoney(r.khoan_khac) : "-"}</td>
                    <td className="py-2 px-1.5 text-right font-bold text-lime-400" style={{ background: "rgba(132,204,22,0.04)" }}>{fMoney(r.thuc_nhan || r.net_salary)}</td>
                    <td className="py-2 px-1.5 text-center">
                      {pctChange !== null ? (
                        <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${parseFloat(pctChange) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none">
                            {parseFloat(pctChange) >= 0 ? (
                              <path d="M3 11 L8 5 L10 7 L13 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            ) : (
                              <path d="M3 5 L8 11 L10 9 L13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            )}
                          </svg>
                          {Math.abs(parseFloat(pctChange))}%
                        </span>
                      ) : <span className="text-[9px] text-slate-600">-</span>}
                    </td>
                  </tr>
                  );
                })}
                {/* Total row */}
                <tr style={{ background: "linear-gradient(90deg, #1a2332 0%, #0f172a 100%)" }} className="border-t-2 border-lime-500/30">
                  <td colSpan={4} className="py-2.5 px-2 font-bold text-slate-100 text-[11px]">TỔNG ({sortedRecords.length} NV)</td>
                  <td className="py-2.5 px-1.5 text-right font-bold text-slate-200">{fMoney(sortedRecords.reduce((a,r) => a + (r.muc_luong || 0), 0))}</td>
                  <td></td>
                  <td className="py-2.5 px-1.5 text-right font-bold text-emerald-400 border-r border-[#1e293b]">{fMoney(sortedRecords.reduce((a,r) => a + (r.phu_cap || 0), 0))}</td>
                  <td colSpan={4} className="border-r border-[#1e293b]"></td>
                  <td colSpan={5}></td>
                  <td className="py-2.5 px-1.5 text-right font-bold text-slate-200 border-r border-[#1e293b]">{fMoney(sortedRecords.reduce((a,r) => a + (r.tong_luong || 0), 0))}</td>
                  <td colSpan={5} className="border-r border-[#1e293b]"></td>
                  <td className="py-2.5 px-1.5 text-right font-bold text-lime-400 text-[11px]" style={{ background: "rgba(132,204,22,0.06)" }}>{fMoney(totalNet)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : subTab === "tam_ung" ? (
        <div className="space-y-4" data-testid="salary-tab-advances">
          {/* Advance summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-orange-600/20 to-orange-500/5 border border-orange-500/20 rounded-xl p-3">
              <p className="text-[9px] text-orange-300 uppercase font-bold">Tổng tạm ứng</p>
              <p className="text-lg font-bold text-orange-300">{fmtVND(advances?.grand_total || 0)}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
              <p className="text-[9px] text-slate-500 uppercase font-bold">Số nhân viên ứng</p>
              <p className="text-lg font-bold text-slate-200">{advances?.total_employees || 0}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
              <p className="text-[9px] text-slate-500 uppercase font-bold">Tổng phiếu</p>
              <p className="text-lg font-bold text-slate-200">{advances?.records?.length || 0}</p>
            </div>
          </div>

          {/* Advance table by employee */}
          {advances?.by_employee?.length > 0 ? (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#0f172a] border-b border-[#334155]">
                    <th className="py-2 px-3 text-left text-slate-400 font-medium">STT</th>
                    <th className="py-2 px-3 text-left text-slate-400 font-medium">Họ tên</th>
                    <th className="py-2 px-3 text-left text-slate-400 font-medium">Chức vụ</th>
                    <th className="py-2 px-3 text-left text-slate-400 font-medium">Đợt ứng</th>
                    <th className="py-2 px-3 text-right text-slate-400 font-medium">Số tiền ứng</th>
                    <th className="py-2 px-3 text-left text-slate-400 font-medium">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {advances.by_employee.map((emp, i) => (
                    <React.Fragment key={emp.name}>
                      {emp.records.map((r, j) => (
                        <tr key={r.id || `${i}-${j}`} className="border-b border-[#334155]/20 hover:bg-[#0f172a]/50">
                          {j === 0 && <td rowSpan={emp.records.length} className="py-1.5 px-3 text-slate-500 align-top">{i + 1}</td>}
                          {j === 0 && <td rowSpan={emp.records.length} className="py-1.5 px-3 text-slate-200 font-medium align-top whitespace-nowrap">{emp.name}</td>}
                          {j === 0 && <td rowSpan={emp.records.length} className="py-1.5 px-3 text-slate-400 align-top">{emp.position}</td>}
                          <td className="py-1.5 px-3 text-sky-400 text-[10px]">{r.department || ""}</td>
                          <td className="py-1.5 px-3 text-right text-orange-400 font-mono">{fmtFull(r.advance || 0)}</td>
                          <td className="py-1.5 px-3 text-slate-500 text-[10px] max-w-[200px] truncate">{(r.note || "").replace(/Ứng: /g, "").replace(/ \| /g, ", ")}</td>
                        </tr>
                      ))}
                      {emp.records.length > 1 && (
                        <tr className="bg-orange-500/5 border-b border-[#334155]/30">
                          <td colSpan={4} className="py-1 px-3 text-[10px] text-orange-300 font-medium text-right">Tổng {emp.name}:</td>
                          <td className="py-1 px-3 text-right text-orange-400 font-bold font-mono">{fmtFull(emp.total)}</td>
                          <td></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {/* Grand total */}
                  <tr className="bg-[#334155]/30 border-t-2 border-[#475569]">
                    <td colSpan={4} className="py-2 px-3 font-bold text-slate-200">TỔNG CỘNG ({advances.total_employees} NV)</td>
                    <td className="py-2 px-3 text-right font-bold text-orange-400 text-sm">{fmtFull(advances.grand_total)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8 text-sm">Chưa có dữ liệu tạm ứng tháng {month}.</p>
          )}
        </div>
      ) : subTab === "phieu_luong" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sortedRecords.map((r, i) => (
            <div key={r.id || i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 hover:border-violet-500/30 transition-colors" data-testid={`salary-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-bold text-slate-200">{r.ten || r.employee_name}</p>
                  <p className="text-[10px] text-slate-500">{r.ma_nv || r.employee_code} - {r.chuc_vu || r.position}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-lime-400">{fmtVND(r.thuc_nhan || r.net_salary || 0)}</p>
                  <p className="text-[9px] text-slate-500 uppercase">Thực nhận</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-slate-500">Mức lương</p><p className="text-slate-200 font-medium">{fmtVND(r.muc_luong || 0)}</p></div>
                <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-slate-500">Phụ cấp</p><p className="text-emerald-400 font-medium">{fmtVND(r.phu_cap || 0)}</p></div>
                <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-slate-500">Số công</p><p className="text-slate-200 font-medium">{r.so_cong || 0}</p></div>
                <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-slate-500">Thâm niên</p><p className="text-amber-400 font-medium">{fmtVND(r.tham_nien || 0)}</p></div>
                <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-slate-500">Tổng lương</p><p className="text-slate-200 font-medium">{fmtVND(r.tong_luong || 0)}</p></div>
                <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-slate-500">Thưởng</p><p className="text-lime-400 font-medium">{fmtVND(r.thuong || 0)}</p></div>
              </div>
              {((r.bhxh || 0) > 0 || (r.dong_phuc || 0) > 0 || (r.tam_ung || 0) > 0 || (r.phat || 0) > 0) && (
                <div className="mt-2 pt-2 border-t border-[#334155]/50 grid grid-cols-4 gap-2 text-[9px]">
                  {(r.bhxh || 0) > 0 && <div><span className="text-slate-500">BHXH:</span> <span className="text-red-400">-{fmtVND(r.bhxh)}</span></div>}
                  {(r.dong_phuc || 0) > 0 && <div><span className="text-slate-500">Đ.Phục:</span> <span className="text-red-400">-{fmtVND(r.dong_phuc)}</span></div>}
                  {(r.tam_ung || 0) > 0 && <div><span className="text-slate-500">Ứng:</span> <span className="text-orange-400">-{fmtVND(r.tam_ung)}</span></div>}
                  {(r.phat || 0) > 0 && <div><span className="text-slate-500">Phạt:</span> <span className="text-red-400">-{fmtVND(r.phat)}</span></div>}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Monthly comparison bar chart */}
          {summary.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2">So sánh quỹ lương các tháng</p>
              <ApexChart type="bar" height={200} options={{
                chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
                plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
                colors: summary.map(s => s.month === month ? '#84cc16' : '#ef4444'),
                xaxis: { categories: summary.map(s => s.month), labels: { style: { fontSize: '10px', colors: '#94a3b8' } } },
                yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '10px', colors: '#94a3b8' } } },
                grid: { borderColor: '#334155', strokeDashArray: 3 },
                tooltip: { theme: 'dark', y: { formatter: v => fmtFull(v) } },
                dataLabels: { enabled: false },
              }} series={[{ name: 'Tổng lương', data: summary.map(s => s.total) }]} />
            </div>
          )}
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-3">Phân bổ theo phòng ban - {month}</p>
            <div className="space-y-2">
              {departments.map(d => {
                const deptRecords = deduped.filter(r => r.department === d);
                const deptTotal = deptRecords.reduce((a, r) => a + (r.thuc_nhan || r.net_salary || 0), 0);
                const pct = totalNet > 0 ? (deptTotal / totalNet * 100) : 0;
                return (
                  <div key={d} className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-300 w-40 truncate">{d.replace("BẢNG LƯƠNG ", "").replace("BẢNG LƯƠNG- ", "")}</span>
                    <div className="flex-1 h-2 bg-[#0f172a] rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="text-[10px] text-slate-400 w-12 text-right">{pct.toFixed(0)}%</span>
                    <span className="text-[10px] text-violet-400 w-24 text-right font-mono">{fmtVND(deptTotal)}</span>
                    <span className="text-[9px] text-slate-500">{deptRecords.length} NV</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
